import { FileDown, Loader2, Mic } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type ChatResourceKind = "pdf" | "voice_note";

export type ChatResourceFooterProps = {
  /** When true, actions render as inactive placeholders. */
  disabled?: boolean;
  /** Which action is running (spinner on that pill). */
  busy?: ChatResourceKind | null;
  onSavePdf?: () => void;
  onVoiceNote?: () => void;
  tips: {
    pdf: string;
    voice_note: string;
  };
  className?: string;
};

const pillClass = cn(
  "inline-flex items-center gap-1.5 rounded-full border border-white/35 bg-white/10 px-3 py-1.5",
  "font-inter text-[11px] font-medium text-white/95 transition-colors backdrop-blur-[1px]",
  "hover:enabled:border-white/55 hover:enabled:bg-white/20",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60",
  "disabled:cursor-not-allowed disabled:opacity-50"
);

export const ChatResourceFooter = ({
  disabled = false,
  busy = null,
  onSavePdf,
  onVoiceNote,
  tips,
  className,
}: ChatResourceFooterProps) => {
  const Pill = ({
    kind,
    icon: Icon,
    label,
    onClick,
    tip,
  }: {
    kind: ChatResourceKind;
    icon: typeof FileDown;
    label: string;
    onClick?: () => void;
    tip: string;
  }) => {
    const isBusy = busy === kind;
    const anotherBusy = busy !== null && busy !== undefined && busy !== kind;
    const noHandler = !onClick;
    const isDisabled = disabled || anotherBusy || noHandler || isBusy;

    const button = (
      <button
        type="button"
        disabled={isDisabled}
        onClick={onClick}
        className={pillClass}
        aria-busy={isBusy}
      >
        {isBusy ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
        ) : (
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        )}
        <span>{isBusy ? "Saving…" : label}</span>
      </button>
    );

    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          {/* span keeps tooltip working even when button is disabled */}
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] font-inter text-xs">
          {tip}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div
      className={cn(
        "relative z-[1] flex w-full flex-col items-center gap-2 px-2 pb-1 sm:flex-row sm:justify-center sm:gap-3",
        className
      )}
    >
      <span className="text-center font-inter text-[11px] uppercase tracking-wide text-white/60 sm:mr-1">
        Turn this into a resource
      </span>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Pill kind="pdf" icon={FileDown} label="Printable handout" onClick={onSavePdf} tip={tips.pdf} />
        <Pill kind="voice_note" icon={Mic} label="Voice Note" onClick={onVoiceNote} tip={tips.voice_note} />
      </div>
    </div>
  );
};
